// LAYOUT-SPEC §0 — program settings, Deliverables section (D110)
//
//   ONE QUESTION  How does this contract treat deliverable due dates and
//                 approvals?
//   PRIMARY OBJECT  The program's deliverable settings (one record).
//   PRIMARY VERB    Save. Gated on area:programs; everyone else reads the
//                 values with the controls disabled.
//
// Each field is sent only when it changed (UpdateDeliverableSettings writes
// only what is present), so saving the switch never rewrites the roll.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraDetailPage, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraSwitchField, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { PageBody } from '../../../../components/layout';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { useCmAccess } from '../access';
import { useDeliverableSettings, useUpdateDeliverableSettings } from '../hooks';
export const ROLL_OPTIONS = [
    {
        value: 'following_federal_workday',
        label: 'Following federal workday'
    },
    {
        value: 'preceding_federal_workday',
        label: 'Preceding federal workday'
    },
    {
        value: 'none',
        label: 'No roll: the date stands'
    },
    {
        value: '',
        label: 'Not stated in the contract'
    }
];
export function ProgramSettingsPage() {
    const { programId } = useProgramContext();
    const settings = useDeliverableSettings(programId);
    if (settings.isLoading || !settings.data) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: "Loading…"
            })
        });
    }
    // Keyed on the stored values: the form seeds from the read once, and a save
    // remounts it on the new values (never a sync effect).
    const s = settings.data;
    return /*#__PURE__*/ _jsx(SettingsForm, {
        programId: programId,
        roll: s.roll,
        rollLocator: s.rollLocator,
        recipients: s.recipients,
        higherTier: s.higherTierSatisfies
    }, `${s.roll}|${s.rollLocator}|${s.recipients.join(',')}|${s.higherTierSatisfies}`);
}
function SettingsForm({ programId, roll: roll0, rollLocator: locator0, recipients: recipients0, higherTier: higher0 }) {
    const access = useCmAccess(programId);
    const save = useUpdateDeliverableSettings(programId, {
        success: (r)=>{
            const n = r.result?.mint;
            const moved = (n?.updated ?? 0) + (n?.inserted ?? 0) + (n?.retired ?? 0);
            return moved ? `Settings saved. ${moved} dates re-scheduled.` : 'Settings saved';
        }
    });
    const [roll, setRoll] = useState(roll0);
    const [locator, setLocator] = useState(locator0);
    const [recipients, setRecipients] = useState(recipients0.join(', '));
    const [higher, setHigher] = useState(higher0);
    const ro = !access.canSettings;
    const recipientList = recipients.split(',').map((r)=>r.trim()).filter(Boolean);
    const dirty = roll !== roll0 || locator !== locator0 || recipientList.join(',') !== recipients0.join(',') || higher !== higher0;
    const onSave = ()=>save.mutate({
            ...roll !== roll0 ? {
                roll
            } : {},
            ...locator !== locator0 ? {
                rollLocator: locator.trim()
            } : {},
            ...recipientList.join(',') !== recipients0.join(',') ? {
                replaceRecipients: true,
                recipients: recipientList
            } : {},
            ...higher !== higher0 ? {
                higherTierSatisfies: higher
            } : {}
        });
    return /*#__PURE__*/ _jsx(SpecraDetailPage, {
        "data-testid": Testids.cmSettings.page,
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            title: "Settings",
            description: "How this contract treats deliverable due dates and approvals.",
            actions: ro ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: onSave,
                disabled: !dirty || save.isPending,
                isLoading: save.isPending,
                "data-testid": Testids.cmSettings.save,
                children: "Save"
            })
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "max-w-2xl space-y-5",
            children: [
                ro ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    "data-testid": Testids.cmSettings.readOnlyNote,
                    children: "Read-only: changing these needs program settings access."
                }) : null,
                roll0 === '' ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    variant: "framed",
                    "data-testid": Testids.cmSettings.rollBanner,
                    children: "The contract’s weekend and holiday rule isn’t recorded, so due dates are shown as the rule computes them, never rolled. State it below once you find it."
                }) : null,
                /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: "Deliverables"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "A due date on a weekend or federal holiday",
                                    hint: "What the contract says happens to it.",
                                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                        value: roll,
                                        disabled: ro,
                                        onChange: (e)=>setRoll(e.target.value),
                                        "data-testid": Testids.cmSettings.roll,
                                        children: ROLL_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                                value: o.value,
                                                children: o.label
                                            }, o.value || 'unstated'))
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Where the contract says so",
                                    hint: "e.g. “Award B.3, p.10”",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: locator,
                                        disabled: ro,
                                        onChange: (e)=>setLocator(e.target.value),
                                        "data-testid": Testids.cmSettings.rollLocator
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Recipients",
                            hint: "Who deliverables go to by default, comma-separated.",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: recipients,
                                disabled: ro,
                                onChange: (e)=>setRecipients(e.target.value),
                                "data-testid": Testids.cmSettings.recipients
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": Testids.cmSettings.higherTier,
                            children: /*#__PURE__*/ _jsx(SpecraSwitchField, {
                                label: "An approval at a higher VA tier also approves",
                                description: "e.g. a COR approval counts where the deliverable’s final tier is VA PM. Contractor and unspecified reviews never count.",
                                controlProps: {
                                    checked: higher,
                                    onCheckedChange: (v)=>setHigher(v === true),
                                    disabled: ro
                                }
                            })
                        })
                    ]
                })
            ]
        })
    });
}
